import React, { useRef, useState, useEffect } from 'react';
import axios from 'axios';
import classes from './Onboarding.module.css';
import OnboardingProgress from './OnboardingProgress';
import { FaRegFileAlt, FaCheck } from 'react-icons/fa';
import { IoCloseCircleOutline } from 'react-icons/io5';
import { useAzurePublicUpload } from '../../hooks/useAzurePublicUpload';
import { RiFileUploadLine } from "react-icons/ri";
import { useTransporterData } from '../../hooks/useTransporterData';
import { Urls } from '../../Urls';
import { useOnboardingMixpanelTrack } from './hooks/useOnboardingMixpanelTrack';

const EXTRACT_STAGES = [
    'Uploading file to secure storage…',
    'Processing document pages…',
    'Extracting content with AI…',
    'Mapping fields to your profile…',
    'Almost there, finalizing…',
];

// Milliseconds after which each stage becomes active (index 0 = immediate)
const STAGE_DELAYS = [0, 4000, 10000, 18000, 26000];

const ACCEPTED_TYPES = '.pdf,.png,.jpg,.jpeg,.xlsx,.xls';
const STEPS = ['Upload Doc', 'Review & Confirm'];

// 3-step left panel labels for consistent UI
const LEFT_STEPS = [
    { label: 'Upload Document', icon: '↑' },
    { label: 'Essential Details', icon: '✦' },
    { label: 'Additional Details', icon: '◉' },
];

const OnboardingUpload = ({ onExtracted, onSwitchPath }) => {
    const [dragging, setDragging] = useState(false);
    const [file, setFile] = useState(null);
    const [extracting, setExtracting] = useState(false);
    const [stageIdx, setStageIdx] = useState(0);
    const [error, setError] = useState('');
    const inputRef = useRef();

    const { data: transporterData } = useTransporterData();
    const { trackOnboardingEvent } = useOnboardingMixpanelTrack();

    // Advance through fake-progress stages while extracting is active
    useEffect(() => {
        if (!extracting) { setStageIdx(0); return; }
        const timers = STAGE_DELAYS.slice(1).map((delay, i) =>
            setTimeout(() => setStageIdx(i + 1), delay)
        );
        return () => timers.forEach(clearTimeout);
    }, [extracting]);

    // Use the Azure upload hook
    const { uploadFile, isUploading, error: azureError } = useAzurePublicUpload();

    /* ── drag handlers ──────────────────────────────────────── */
    const onDragOver = e => { e.preventDefault(); setDragging(true); };
    const onDragLeave = () => setDragging(false);
    const onDrop = e => {
        e.preventDefault();
        setDragging(false);
        const dropped = e.dataTransfer.files[0];
        if (dropped) handleFile(dropped);
    };

    const handleFile = f => {
        setError('');
        setFile(f);
        trackOnboardingEvent(
            'onboarding upload',
            'Document selected for AI extraction',
            { file_name: f.name, file_type: f.name.split('.').pop().toLowerCase() }
        );
    };

    const removeFile = () => {
        trackOnboardingEvent(
            'onboarding upload',
            'Selected document removed',
            { file_name: file?.name }
        );
        setFile(null);
        setError('');
        if (inputRef.current) inputRef.current.value = '';
    };

    /* ── upload & extract ───────────────────────────────────── */
    const handleUpload = async () => {
        if (!file) {
            setError('Please select a file first.');
            return;
        }
        setExtracting(true);
        setError('');
        trackOnboardingEvent(
            'onboarding upload',
            'AI extraction started',
            {
                file_name: file.name,
                file_type: file.name.split('.').pop().toLowerCase(),
                transporter_id: transporterData?.transporter_id,
            }
        );
        try {
            const customFilename = `${transporterData.transporter_id}/${file.name}`;

            // Step 1: Upload to Azure Blob Storage
            const { blobUrl, fileName } = await uploadFile(file, customFilename);

            // Step 2: Use the file URL and send it to your API for processing
            const formData = new FormData();
            formData.append('file_url', blobUrl);

            const response = await axios.post(Urls.profileFileUpload, formData);

            const data = response.data;
            if (data && data.fields) {
                trackOnboardingEvent(
                    'onboarding upload',
                    'AI extraction completed successfully',
                    { transporter_id: transporterData?.transporter_id, file_name: file.name }
                );
                onExtracted(data.fields);
            } else {
                // Attempt fallback — maybe the response IS the fields object
                trackOnboardingEvent(
                    'onboarding upload',
                    'AI extraction completed successfully',
                    { transporter_id: transporterData?.transporter_id, file_name: file.name }
                );
                onExtracted(typeof data === 'object' ? data : {});
            }
        } catch (err) {
            console.error('Upload error:', err);
            const errMsg =
                err?.response?.data?.detail ||
                'Failed to process the document. Please try again or fill manually.';
            trackOnboardingEvent(
                'onboarding upload',
                'AI extraction failed',
                { error: errMsg, transporter_id: transporterData?.transporter_id }
            );
            setError(errMsg);
        } finally {
            setExtracting(false);
        }
    };

    /* ── render ─────────────────────────────────────────────── */
    return (
        <div className={classes.onboardingWrap}>
            <div className={classes.splitCard}>

                {/* ═══ LEFT PANEL ═══ */}
                <div className={classes.leftPanel}>
                    <div className={classes.logoWrap}>
                        <img src={Urls.LorriLogoLogin} alt="LoRRI" className={classes.logo} />
                    </div>
                    <p className={classes.subheading} style={{ marginBottom: 0 }}>Your profile is being set up</p>

                    {/* 3-step flow tracker */}
                    <div className={classes.reviewLeftSteps} style={{ marginTop: 24 }}>
                        {LEFT_STEPS.map((s, i) => {
                            const isDone = false; // Never done on step 0
                            const isActive = i === 0; // Upload Document is active
                            return (
                                <div
                                    key={s.label}
                                    className={`${classes.reviewLeftStep} ${isActive ? classes.reviewLeftStepActive : ''} ${isDone ? classes.reviewLeftStepDone : ''}`}
                                >
                                    <div className={classes.reviewLeftStepDot}>
                                        {isDone ? <FaCheck size={9} /> : i + 1}
                                    </div>
                                    <span>{s.label}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* ═══ RIGHT PANEL ═══ */}
                <div className={classes.rightPanel} style={{ display: 'flex', flexDirection: 'column' }}>

                    <OnboardingProgress steps={STEPS} current={0} />

                    <h1 className={classes.heading}>Upload your company document</h1>
                    <p className={classes.subheading}>
                        We'll extract your details automatically using AI. Supports PDF,
                        Word, or image files.
                    </p>

                    {extracting ? (
                        /* ── Fake-progress loading state ── */
                        <div className={classes.extractingWrap} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                            <div className={classes.extractingSpinner} />
                            <div className={classes.extractingText} style={{ marginBottom: 20 }}>
                                Analysing your document…
                            </div>

                            {/* Stage ladder */}
                            <div className={classes.extractStages}>
                                {EXTRACT_STAGES.map((label, i) => {
                                    const done = i < stageIdx;
                                    const active = i === stageIdx;
                                    return (
                                        <div key={i} className={`${classes.extractStageRow} ${active ? classes.extractStageActive : ''} ${done ? classes.extractStageDone : ''}`}>
                                            <div className={`${classes.extractStageDot}${done ? ` ${classes.extractStageDotDone}` : ''}`}>
                                                {done ? <FaCheck size={8} /> : active ? <span className={classes.extractStagePulse} /> : null}
                                            </div>
                                            <span>{label}</span>
                                        </div>
                                    );
                                })}
                            </div>

                            <p style={{ fontSize: '11px', color: '#b0aed4', marginTop: 20 }}>
                                Please don't refresh the page.
                            </p>
                        </div>
                    ) : (
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', paddingRight: 4 }}>
                            {/* ── Drop zone ── */}
                            <div
                                className={`${classes.dropZone} ${dragging ? classes.dragging : ''}`}
                                onDragOver={onDragOver}
                                onDragLeave={onDragLeave}
                                onDrop={onDrop}
                                onClick={() => !file && inputRef.current?.click()}
                                id="onboarding-drop-zone"
                                style={{ flex: 1, minHeight: 250 }}
                            >
                                <input
                                    ref={inputRef}
                                    type="file"
                                    accept={ACCEPTED_TYPES}
                                    style={{ display: 'none' }}
                                    onChange={e => e.target.files[0] && handleFile(e.target.files[0])}
                                />
                                {!file ? (
                                    <>
                                        <div className={classes.dropIcon}><RiFileUploadLine /></div>
                                        <div className={classes.dropTitle}>Drag & drop your file here</div>
                                        <div className={classes.dropSub}>or</div>
                                        <span className={classes.browseLink}>Browse files</span>
                                        <div className={classes.uploadFormats}>
                                            PDF · XLSX · PNG · JPG
                                        </div>
                                    </>
                                ) : (
                                    <div className={classes.fileChosen} onClick={e => e.stopPropagation()}>
                                        <FaRegFileAlt className={classes.fileChosenIcon} />
                                        <div className={classes.fileChosenName}>{file.name}</div>
                                        <span
                                            className={classes.removeFile}
                                            onClick={removeFile}
                                            title="Remove file"
                                        >
                                            <IoCloseCircleOutline />
                                        </span>
                                    </div>
                                )}
                            </div>

                            {error && <div className={classes.uploadError}>{error}</div>}

                            {/* ── Sticky footer / buttons ── */}
                            <div className={classes.reviewStickyFooter} style={{ borderTop: 'none', paddingTop: 20 }}>
                                <button
                                    className={classes.btnSecondary}
                                    onClick={onSwitchPath}
                                    id="onboarding-upload-back"
                                    disabled={extracting}
                                >
                                    ← Back
                                </button>
                                <button
                                    className={classes.btnPrimary}
                                    onClick={handleUpload}
                                    disabled={!file || extracting}
                                    id="onboarding-upload-submit"
                                >
                                    Extract Details →
                                </button>
                            </div>

                            <div className={classes.centerText} style={{ marginTop: 12 }}>
                                <button className={classes.linkBtn} onClick={onSwitchPath} disabled={extracting}>
                                    Prefer to fill manually instead?
                                </button>
                            </div>
                        </div>
                    )}
                </div>{/* end rightPanel */}
            </div>
        </div>
    );
};


export default OnboardingUpload;
